import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  FaUsers,
  FaUserFriends,
  FaPaperPlane,
  FaInfoCircle,
} from "react-icons/fa";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Input from "../../components/ui/Input";
import Textarea from "../../components/ui/Textarea";
import Button from "../../components/ui/Button";
import MultiSelect from "../../components/ui/MultiSelect";
import { notificationsService } from "../../services/notifications.service";
import { usersService } from "../../services/users.service";
import { getErrorMsg } from "../../utils/error";
import { NotificationFormData } from "./notification-sender.types";

const schema = yup.object().shape({
  userIds: yup
    .array()
    .of(yup.string().required())
    .when("isBroadcast", {
      is: false,
      then: (schema) => schema.min(1, "At least one user must be selected"),
      otherwise: (schema) => schema.notRequired(),
    }),
  isBroadcast: yup.boolean(),
  title: yup.string().required("Title is required"),
  message: yup.string().required("Message is required"),
  category: yup.string().optional(),
});

const NotificationSender = () => {
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [userOptions, setUserOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<NotificationFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
    defaultValues: {
      userIds: [],
      isBroadcast: false,
    },
  });

  const isBroadcast = watch("isBroadcast");

  useEffect(() => {
    const fetchUsers = async () => {
      setFetchingUsers(true);
      try {
        // Fetch users (limit to 1000 for now, should implement search in future)
        const response = await usersService.list({ take: 1000 });
        const options = response.data.map((user) => ({
          value: user.id,
          label:
            `${user.fullName || user.email} (${user.email})`,
        }));
        setUserOptions(options);
      } catch (err) {
        console.error("Failed to fetch users", err);
      } finally {
        setFetchingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const onSubmit = async (data: NotificationFormData) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await notificationsService.sendUser({
        userIds: data.userIds,
        isBroadcast: data.isBroadcast,
        title: data.title,
        message: data.message,
        category: data.category,
      });
      setSuccess("Notification sent successfully");
      reset({
        userIds: [],
        isBroadcast: false,
        title: "",
        message: "",
        category: "",
      });
      // Scroll to top to show success message
      window.scrollTo(0, 0);
    } catch (err) {
      setError(getErrorMsg(err, "Failed to send notification"));
      window.scrollTo(0, 0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout title={t("send_notification") || "Send Notification"}>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Status Messages */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 p-4 rounded-lg flex items-center">
            <FaInfoCircle className="mr-2" />
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 p-4 rounded-lg flex items-center">
            <FaInfoCircle className="mr-2" />
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Recipients Section */}
          <Card className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-100 dark:border-gray-700 pb-2">
              Recipients
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    isBroadcast
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500"
                      : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700"
                  }`}
                  onClick={() => setValue("isBroadcast", true)}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-2 rounded-full ${isBroadcast ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-500"}`}
                    >
                      <FaUsers size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        Broadcast to All
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Send to all registered users
                      </p>
                    </div>
                    <input
                      type="radio"
                      className="ml-auto h-4 w-4 text-blue-600 focus:ring-blue-500"
                      checked={isBroadcast === true}
                      onChange={() => setValue("isBroadcast", true)}
                    />
                  </div>
                </div>

                <div
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    !isBroadcast
                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-500"
                      : "border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700"
                  }`}
                  onClick={() => setValue("isBroadcast", false)}
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`p-2 rounded-full ${!isBroadcast ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-500"}`}
                    >
                      <FaUserFriends size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        Specific Users
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Select individual recipients
                      </p>
                    </div>
                    <input
                      type="radio"
                      className="ml-auto h-4 w-4 text-blue-600 focus:ring-blue-500"
                      checked={isBroadcast === false}
                      onChange={() => setValue("isBroadcast", false)}
                    />
                  </div>
                </div>
              </div>

              {!isBroadcast && (
                <div className="mt-4 animate-fadeIn">
                  <Controller
                    name="userIds"
                    control={control}
                    render={({ field }) => (
                      <MultiSelect
                        label="Select Users"
                        options={userOptions}
                        value={field.value || []}
                        onChange={field.onChange}
                        error={errors.userIds?.message}
                        placeholder={
                          fetchingUsers
                            ? "Loading users..."
                            : "Search and select users..."
                        }
                        disabled={fetchingUsers}
                      />
                    )}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Search by name or email. Multiple users can be selected.
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Content Section */}
          <Card className="p-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4 border-b border-gray-100 dark:border-gray-700 pb-2">
              Message Content
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Title"
                  {...register("title")}
                  error={errors.title?.message}
                  placeholder="Notification title"
                />
                <Input
                  label="Category (Optional)"
                  {...register("category")}
                  error={errors.category?.message}
                  placeholder="e.g. System, Promotion, Alert"
                />
              </div>

              <Textarea
                label="Message"
                {...register("message")}
                error={errors.message?.message}
                placeholder="Type your message here..."
                rows={5}
              />
            </div>
          </Card>

          <div className="flex justify-end pt-2">
            <Button
              type="submit"
              isLoading={loading}
              className="px-8 flex items-center space-x-2"
            >
              <FaPaperPlane className="mr-2" />
              <span>{t("send_notification") || "Send Notification"}</span>
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default NotificationSender;
